// Live "answer view" renderer — the client half of the `render_answer_ui`
// workspace tool. Simone answers a data question by DRAWING a small DOMQL view
// instead of (or alongside) prose: the AI-authored source is statically
// audited (auditExtensionSource — the same gate every extension mount runs),
// parsed through the same guarded freestyle parser the canvas injectors use,
// registered as a TRANSIENT answer page on root._aiAnswerPages, and opened in
// the shell PageSlot — the same split sheet every app page opens in, so the
// user keeps chatting beside it. Nothing persists: answer views live only in root state
// for this session; asking again renders a fresh route.
//
// Route shape: '/answer/<slug>-<seq>' — seq keeps every render unique so the
// PageSlot keep-alive (same-path remounts are skipped) can never pin a stale
// view when the same question is asked twice. The map is pruned to the last
// few entries so a long chat session can't grow root state unboundedly.

// SANDBOX (ISOLATED-RUNTIME-PLAN §8, flag appsSandbox, default off): the
// workspace decides; the def comes from its registered builder (canvas cannot
// import workspace). Flag on with no builder registered: fail closed.
const _sandboxOn = (el) => {
  const root = (el.getRootState && el.getRootState()) || (el.state && (el.state.root || el.state))
  return el.call('generatedAppSandboxOn', root) === true
}

const MAX_ANSWER_PAGES = 8

let _answerSeq = 0

const _canvasSlug = (title) =>
  String(title || 'answer')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'answer'

export function renderAnswerUi(payload = {}) {
  // el.call convention: `this` is the calling element; direct-call convention
  // (unit tests): first arg is the element, second the payload.
  const el = this && this.getRoot ? this : arguments[0]
  const args = this && this.getRoot ? payload : arguments[1] || {}
  const title = String(args.title || 'Answer').trim() || 'Answer'
  const source = String(args.source || '')
  if (!source.trim()) return { ok: false, error: 'source is required' }

  // SECURITY GATE — the same static audit every sibling AI-mount path runs
  // BEFORE model-authored source is eval'd (buildInlineExtensionDef /
  // buildInlineMultiPageDef / renderExtensionInPane / renderMultiPageApp in
  // workspace/functions/index.js): reject source reaching for raw network/
  // storage/eval/realm-escape primitives. Answer views mount into the LIVE
  // SHELL realm (live context + design system + root state), so this boundary
  // matters MORE here, not less. auditExtensionSource is registered by the
  // workspace functions barrel — canvas cannot import it (cross-app eslint
  // boundary); el.call is the house resolution mechanism, exactly how
  // parseFreestyleSource is reached below. FAIL CLOSED: if the auditor is not
  // registered in this context (standalone canvas wires no AI transport, so
  // renderAnswerUi has no caller there today), refuse the mount rather than
  // mounting unaudited source. The { ok: false, error, violations } refusal
  // shape is the sibling contract — simoneAiTransport returns it to the model
  // so it can self-correct, the same loop create_app uses.
  const audit = el.call('auditExtensionSource', source)
  if (!audit || audit.ok !== true) {
    return {
      ok: false,
      error:
        (audit && audit.error) ||
        'security audit unavailable in this context — refusing to mount unaudited source',
      violations: (audit && audit.violations) || ['audit unavailable']
    }
  }

  const root = el.getRootState?.() || el.state?.root || el.getRoot?.()?.state
  if (!root || typeof root.update !== 'function') {
    return { ok: false, error: 'no root state — cannot render the answer view' }
  }

  // CONTAINMENT GATE (Nika 2026-10-01 "Cut access now") — the AST gate the
  // workspace registers (parseGeneratedAppSource, generatedAppContainment.js):
  // the answer view mounts in the LIVE shell, so the page may call only the
  // allowlisted functions and reach nothing of the shell. FAIL CLOSED like
  // the audit above: no registered gate → no mount.
  if (_sandboxOn(el)) {
    const built = el.call('buildSandboxedAppDef', { name: title, source })
    if (!built || typeof built !== 'object') {
      return {
        ok: false,
        error: 'sandbox builder unavailable — refusing to mount',
        violations: ['gate unavailable']
      }
    }
    if (built.blocked)
      return {
        ok: false,
        blocked: true,
        error: built.error,
        violations: built.violations,
        ...(built.hint ? { hint: built.hint } : {})
      }
    if (!built.ok) return { ok: false, error: `source failed to parse: ${built.error}` }
    return _registerAnswerPage(el, root, title, source, built.def, args)
  }
  let parsed
  try {
    // `lint` — an answer view is also refused for a bare-number size or a
    // colour the design system does not have (both render nothing), with a
    // hint naming each (GENERATED-VIEW-STYLE-LINT-1).
    parsed = el.call('parseGeneratedAppSource', source, { lint: true })
  } catch (err) {
    return { ok: false, error: `source failed to parse: ${err?.message || err}` }
  }
  if (!parsed || typeof parsed !== 'object') {
    return {
      ok: false,
      error: 'containment gate unavailable in this context — refusing to mount unaudited source',
      violations: ['gate unavailable']
    }
  }
  if (parsed.blocked) {
    return {
      ok: false,
      blocked: true,
      error: parsed.error,
      violations: parsed.violations,
      ...(parsed.hint ? { hint: parsed.hint } : {})
    }
  }
  if (!parsed.ok) return { ok: false, error: `source failed to parse: ${parsed.error}` }
  const def = parsed.def
  if (!def || typeof def !== 'object') {
    return { ok: false, error: 'source parsed to nothing renderable' }
  }

  // Accept the freestyler wrapper shape ({ components, icons, page }) as well
  // as a bare page def. Wrapper components register ADDITIVELY — an answer view
  // may bring a small helper component, but it must never shadow a registered
  // one (that would break every other consumer of that name).
  const page = def.page && typeof def.page === 'object' ? def.page : def
  if (def.components && typeof def.components === 'object') {
    const ctx = el.context || el.getRoot?.()?.context
    if (ctx && ctx.components) {
      for (const key of Object.keys(def.components)) {
        if (!ctx.components[key]) ctx.components[key] = def.components[key]
      }
    }
  }

  return _registerAnswerPage(el, root, title, source, page, args)
}

// An answer view is a CARD OF CONTENT, never a full-window app
// (ANSWER-VIEW-CONTENT-SIZED-1). The PageSlot host is a row flex box that
// stretches its page root to the window's height, and models often put
// `height` / `minHeight: '100%'` on the root too — measured on dev 2026-10-04:
// three stat tiles drawn as a flex row grew to ~1000 px tall, empty below the
// numbers. The registered ROOT sizes to its content: it does not stretch
// (alignSelf flex-start, unless the view sets its own), and a height or
// min-height that only means "fill the window" (a percentage or a viewport
// unit) becomes auto / 0. The inline thread mount normalizes the same way
// (assistantBlockMountView). Children keep every size they declare.
const _fillsWindow = (v) =>
  typeof v === 'string' && /^\s*\d+(\.\d+)?\s*(%|vh|dvh|svh|lvh)\s*$/.test(v)
export const answerViewContentSized = (page) => {
  const out = { ...page }
  if (_fillsWindow(out.height)) out.height = 'auto'
  if (_fillsWindow(out.minHeight)) out.minHeight = '0'
  if (out.alignSelf == null) out.alignSelf = 'flex-start'
  return out
}

function _registerAnswerPage(el, root, title, source, page, args) {
  const route = `/answer/${_canvasSlug(title)}-${++_answerSeq}`
  const entries = Object.entries(root._aiAnswerPages || {}).slice(-(MAX_ANSWER_PAGES - 1))
  // Stash the raw source in metadata so the PageSlot "Save as app" button can
  // recover the exact DOMQL to persist as an extension (the parsed def alone
  // can't be re-serialized). __source is inert on the mounted def.
  entries.push([
    route,
    {
      ...answerViewContentSized(page),
      // `contentSized` — the PageSlot keeps this page's own height instead of
      // forcing it to fill the window (PageSlot _pageFillProps).
      metadata: { ...(page.metadata || {}), title, __source: source, contentSized: true }
    }
  ])

  // `open: false` — registered, not opened: the caller draws the view inline
  // (the assistant's main thread, AI_ASSISTANT_SPEC v1 §3), so no split pane
  // takes the home while that thread is on screen; "Open in split" is the
  // block's own action.
  const open = args.open !== false
  root.update(
    {
      _aiAnswerPages: Object.fromEntries(entries),
      ...(open ? { _canvasSlot: route, _slotFullscreen: null } : {})
    },
    { preventFetch: true }
  )
  return { ok: true, opened: route, title, ...(open ? {} : { inline: true }) }
}
